// Where the bytes actually live, and the only file that knows.
//
// Everything above this reads and writes one JSON document and never touches `localStorage`.
//
// EVERYTHING IS A PROMISE, though the store is read synchronously. That indirection was bought for
// a second transport: until the 2026-09-12 removal of the per-tool subdomains, this site was served
// from nineteen origins, and a browser store is cut along origins — answers kept while filling a
// form on the apex were invisible from `birth-chart-calculator.…`. A subdomain page therefore asked
// an invisible frame served BY the apex, over postMessage, which is asynchronous. One origin left,
// one transport, and the promises stay: they cost a few `await`s and they are what the callers
// already speak.
//
// ONE KEY FOR THE WHOLE STORE, not one per entry. A key per entry writes less on each save and
// enumerates badly — there is no ordering, no atomic multi-key write, and `key(i)` over a store
// somebody else also writes to is a walk with no guarantees. One document is read whole, written
// whole and exported as-is, which is what makes import/export a copy rather than a migration. The
// cost is real and bounded: saving one answer rewrites every answer, which at the size of typed
// form replies is a rounding error next to the 5 MB a store gets.

const KEY = 'arcate:mydata:v1';

/* An empty document, and the shape every reader can rely on. Returned for a store that has never
   been written AND for one that cannot be read, deliberately: a caller that had to tell the two
   apart would branch on it everywhere, and there is nothing useful either could do differently.
   What a caller DOES need to tell apart is a store that refuses to be WRITTEN — and that is what
   the write path reports, because an entry shown as kept and gone on the next load is a lie. */
function blank() {
	return { version: 1, entries: {} };
}

/* Whether this document is one of ours. Anything else is treated as absent rather than repaired: a
   store we cannot vouch for is not a store to start editing. */
function sound(doc) {
	return !!doc && doc.version === 1 && !!doc.entries && typeof doc.entries === 'object';
}

// ── The direct transport ────────────────────────────────────────────────────────────────────────

function readDirect() {
	let parsed;
	try {
		parsed = JSON.parse(localStorage.getItem(KEY) || 'null');
	} catch (ignored) {
		// Private browsing refuses the accessor itself, and a damaged value throws on parse. Both
		// mean the same thing to a reader: there is nothing here.
		return blank();
	}
	return sound(parsed) ? parsed : blank();
}

function writeDirect(doc) {
	try {
		localStorage.setItem(KEY, JSON.stringify(doc));
		return true;
	} catch (ignored) {
		// Quota exceeded, or a browser refusing storage outright.
		return false;
	}
}

function probeDirect() {
	// Answered by trying, not by feature-detecting: `typeof localStorage` is truthy in a Safari
	// private window that throws on the first write, and a screen that believed the type check would
	// promise a store it does not have. The probe writes and removes its own key, never ours.
	try {
		const probe = KEY + ':probe';
		localStorage.setItem(probe, '1');
		localStorage.removeItem(probe);
		return true;
	} catch (ignored) {
		return false;
	}
}

// ── What the rest of the store sees ─────────────────────────────────────────────────────────────

/** The whole document. Never rejects: a store that cannot be reached reads as an empty one. */
export function read() {
	return Promise.resolve(readDirect());
}

/** Writes the whole document. Resolves to whether it was actually kept — never throws. */
export function write(doc) {
	return Promise.resolve(writeDirect(doc));
}

/** Whether this browser will store anything at all. */
export function usable() {
	return Promise.resolve(probeDirect());
}

export { blank, sound, KEY, probeDirect, readDirect, writeDirect };
